import { 
    Button, 
    CardActions, 
    Grid, 
    Typography, 
    InputAdornment,
    InputLabel,
    MenuItem,
    TextField,
    FormHelperText
} from "@mui/material";
import moment from "moment";
import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { register, submitRequestDemo, updateRequestDemo } from '../api';
import { 
    StyledCircularProgress, 
    StyledLoginCard, 
    StyledLoginForm } from "../StyledComponents";
import { 
    RequestDemo, 
    UsersAgeDistribution, 
    UsersMentalHealthDistribution 
} from '../types/RequestDemo';
import AlertDialog from "./AlertDialog";
import SimpleSnackBar, { SnackBarProp } from "./SimpleSnackBar";

interface Prop {
    data?: RequestDemo;
    onClose?: () => void;
}

export default function MissionDataGrid(props: Prop) {
    const {t} = useTranslation();
    const navigate = useNavigate();
  
    const newRequestDemo: RequestDemo = props.data !== undefined ?
      props.data :
      {
        email: "",
        email_confirmation: "",
        name: "",
        company_name: "",
        contact_number: "",
        company_address: "",
        company_type: "",
        company_type_other: "",
        no_of_user: "",
        is_approved: false,
        created_user_id: 0,
        created_user_at: 0,
        created_at: 0,
        users_age_distribution: {
          group_55_65: 0,
          group_65_75: 0,
          group_75_85: 0,
          group_85_above: 0
        },
        users_mental_health_distribution: {
          healthy: 0,
          suspect: 0,
          early: 0,
          middle: 0,
          late: 0
        }
      };
  
    type InputError = {
      isError: boolean,
      reason: string | null
    }
  
    const [registerInfo, setRegisterInfo] = useState<RequestDemo>(newRequestDemo);
    const [emailError, setEmailError] = useState<InputError>({ isError: false, reason: null });
    const [confirmEmailError, setConfirmEmailError] = useState<InputError>({ isError: false, reason: null });
    const [nameError, setNameError] = useState<InputError>({ isError: false, reason: null });
    const [companyNameError, setCompanyNameError] = useState<InputError>({ isError: false, reason: null });
    const [contactNumberError, setContactNumberError] = useState<InputError>({ isError: false, reason: null });
    const [noOfUserError, setNoOfUserError] = useState<InputError>({ isError: false, reason: null });
    const [companyTypeError, setCompanyTypeError] = useState<InputError>({ isError: false, reason: null });
    const [ageGroupError, setAgeGroupError] = useState<InputError>({ isError: false, reason: null});
    const [mentalHealthGroupError, setMentalHealthGroupError] = useState<InputError>({ isError: false, reason: null});
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [openRegisterSuccessfullyDialog, setOpenRegisterSuccessfullyDialog] = useState(false);

    const noOfUserOptions = [
      {
          value: "below_100",
          label: t("requestDemoTable.below_100")
      },
      {
          value: "100-300",
          label: "100-300"
      },
      {
          value: "300-500",
          label: "300-500"
      },
      {
          value: "more_than_500",
          label: t("requestDemoTable.more_than_500")
      }
    ];

    const CompanyTypeOptions = [
      {
        value: "sponsered",
        label: t("requestDemoTable.comp_type_sponsered")
      },
      {
        value: "daily",
        label: t("requestDemoTable.comp_type_daily")
      },
      {
        value: "district_daily",
        label: t("requestDemoTable.comp_type_district_daily")
      },
      {
        value: "disrict",
        label: t("requestDemoTable.comp_type_district")
      },
      {
        value: "neighbor",
        label: t("requestDemoTable.comp_type_neighbor")
      }, 
      {
        value: "elderly_home",
        label: t("requestDemoTable.comp_type_elderly_home")
      }, 
      {
        value: "private",
        label: t("requestDemoTable.comp_type_private")
      },
      {
        value: "integrated",
        label: t("requestDemoTable.comp_type_integrated")
      },
      {
        value: "professional",
        label: t("requestDemoTable.comp_type_professional")
      }, 
      {
        value: "rehab_center",
        label: t("requestDemoTable.comp_type_rehab_center")
      }, 
      {
        value: "rehab_home",
        label: t("requestDemoTable.comp_type_rehab_home")
      },
      {
        value: "other",
        label: t("common.other")
      }
    ];

    useEffect(() => {
      if (registerInfo.users_age_distribution) {
        let sum: number = 0
        Object.values(registerInfo.users_age_distribution).forEach(val => {
          if (val === "") {
            val = 0
          }
          sum += val
        }
      )
      if (sum !== 100 && sum!==0) {
        setAgeGroupError({ isError: true, reason: t("msg.sum_not_equal_to_100") })
      }
    }}, [registerInfo.users_age_distribution])

    useEffect(() => {
      if (registerInfo.users_mental_health_distribution) {
        let sum: number = 0
        Object.values(registerInfo.users_mental_health_distribution).forEach(val => {
          if (val === "") {
            val = 0
          }
          sum += val
        }
      )
      if (sum !== 100 && sum!==0) {
        setMentalHealthGroupError({ isError: true, reason: t("msg.sum_not_equal_to_100") })
      }
    }}, [registerInfo.users_mental_health_distribution])
  
    const onRegisterFieldChange = (name: keyof RequestDemo | keyof UsersAgeDistribution | keyof UsersMentalHealthDistribution, value: any) => {
      setRegisterInfo({ ...registerInfo, [name]: value });
  
      if (name === "email") {
        if (value.length === 0) 
          setEmailError({ isError: true, reason: t("error.please_enter")})
        else if (!validateEmail(value))
          setEmailError({ isError: true, reason: t("error.email_format") });
        else {
          setEmailError({ isError: false, reason: null });
        }
      }
  
      if (name === "email_confirmation") {
        if (props.data === undefined) {
          if (value.length === 0)
            setConfirmEmailError({ isError: true, reason: t("error.please_enter")})
          else if (value !== registerInfo.email)
            setConfirmEmailError({ isError: true, reason: t("register.error_text_confirm_email_not_equal") });
          else
            setConfirmEmailError({ isError: false, reason: null });
        }
      }
  
      if (name === "name") {
        if (value.length === 0) {
          setNameError({ isError: true, reason: t("error.please_enter")})
        }
        else
          setNameError({ isError: false, reason: null });
      }
  
      if (name === "company_name") {
        if (value.length === 0) 
          setCompanyNameError({ isError: true, reason: t("error.please_enter")})
        else {
          setCompanyNameError({ isError: false, reason: null });
        }
      }
  
      if (name === "company_type" || name === "company_type_other") {
        setCompanyTypeError({ isError: false, reason: null });
      }
  
      if (name === "contact_number") {
        if (value.length === 0) 
          setCompanyNameError({ isError: true, reason: t("error.please_enter")})
        else if (!validatePhone(value)) {
          setContactNumberError({ isError: true, reason: t("requestDemoTable.reason_not_only_digit") });
        }
        else {
          setContactNumberError({ isError: false, reason: null });
        }
      }
  
      if (name === "no_of_user") {
        setNoOfUserError({ isError: false, reason: null });
      }

      setErrorSnackBar({ ...errorSnackBar, "open": false });
    }
  
    function validateEmail(email: string) {
      const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return regex.test(email.toLowerCase());
    }
  
    function validatePhone(phone: string) {
      const regex = /^[0-9]*$/;
      return regex.test(phone);
    }
  
    const onLoginButtonClick = (event: React.MouseEvent) => {
      event.preventDefault();
      let hasError = false;
      if (registerInfo.email === "") {
        setEmailError({ isError: true, reason: t("error.please_enter") });
        hasError = true;
      }
      else if (!validateEmail(registerInfo.email)) {
        setEmailError({ isError: true, reason: t("error.email_format") });
        hasError = true;
      }
      if (registerInfo.email_confirmation === "" && props.data === undefined) {
        setConfirmEmailError({ isError: true, reason: t("error.please_enter") });
        hasError = true;
      }
      if (registerInfo.email !== registerInfo.email_confirmation && props.data === undefined) {
        setConfirmEmailError({ isError: true, reason: t("error.email_confirm_not_equal") });
        hasError = true;
      }
      if (registerInfo.name === "") {
        setNameError({ isError: true, reason: t("error.please_enter") });
        hasError = true;
      }
      if (registerInfo.company_name === "") {
        setCompanyNameError({ isError: true, reason: t("error.please_enter")});
        hasError = true;
      }
      if (registerInfo.contact_number === "") {
        setContactNumberError({ isError: true, reason: t("error.please_enter") });
        hasError = true;
      } else if (!validatePhone(registerInfo.contact_number)) {
        setContactNumberError({ isError: true, reason: t("requestDemoTable.reason_not_only_digit") });
        hasError = true;
      }
      if (registerInfo.no_of_user === "") {
        setNoOfUserError({ isError: true, reason: t("error.please_choose")});
        hasError = true;
      }
      if (registerInfo.company_type === "") {
        setCompanyTypeError({ isError: true, reason: t("error.please_choose") });
        hasError = true;
      }
      if (registerInfo.company_type === "other" && registerInfo.company_type_other === "") {
        setCompanyTypeError({ isError: true, reason: t("error.please_enter") });
        hasError = true;
      }
      if (hasError === false) {
        setIsSubmitting(true);
        console.log(props.data)
        if (props.data === undefined) {
          registerInfo.created_at = moment().valueOf();
          submitRequestDemo(registerInfo).then((data) => {
            console.log(data);
            setOpenRegisterSuccessfullyDialog(true);
            setRegisterInfo(newRequestDemo);
          }, (err) => {
            console.log(err);
            let errorMessage: string;
            err.message !== undefined ? errorMessage = err.message : errorMessage = t("error.unknown");
            setErrorSnackBar({ ...errorSnackBar, message: errorMessage, open: true });
          }).finally(() => {
            setIsSubmitting(false)
          });
        } else {
          if (props.data.id !== undefined) {
            updateRequestDemo(props.data.id, registerInfo).then((data) => {
              console.log(data);
              setOpenRegisterSuccessfullyDialog(true);
            }, (err) => {
              console.log(err);
              let errorMessage: string;
              err.message !== undefined ? errorMessage = err.message : errorMessage = "Unknown error occurred.";
              setErrorSnackBar({ ...errorSnackBar, message: errorMessage, open: true });
            }).finally(() => {
              setIsSubmitting(false)
            });
          }
        }
      } else {
        setErrorSnackBar({ ...errorSnackBar, variant: "error", message: t("requestDemoTable.error_invalid_data_exist"), open: true });
      }
    }
  
    const onUsersAgeDistributionChange = (name: keyof UsersAgeDistribution) => (event: React.ChangeEvent<HTMLInputElement>) => {
      let age_percentage = event.target.value
      if (age_percentage.length === 0) {
        const users_age_distribution = { ...registerInfo.users_age_distribution, [name]: "" };
        setRegisterInfo({ ...registerInfo, users_age_distribution: users_age_distribution });
      } 
      else {
        if (typeof parseInt(age_percentage) === "number") {
          if (Number(age_percentage) >= 0) {
            setAgeGroupError({ isError: false, reason: null });
            const users_age_distribution = { ...registerInfo.users_age_distribution, [name]: parseInt(age_percentage) };
            setRegisterInfo({ ...registerInfo, users_age_distribution: users_age_distribution });
          }
        }
      }
    }
  
    const onUsersMentalHealthDistribution = (name: keyof UsersMentalHealthDistribution) => (event: React.ChangeEvent<HTMLInputElement>) => {
      let mental_health_percentage = event.target.value
      if (mental_health_percentage.length === 0) {
        let users_mental_health_distribution = { ...registerInfo.users_mental_health_distribution, [name]: "" };
        setRegisterInfo({ ...registerInfo, users_mental_health_distribution: users_mental_health_distribution });
      } 
      else {
        if (typeof parseInt(mental_health_percentage) === "number") {
          if (Number(mental_health_percentage) >= 0) {
            setMentalHealthGroupError({isError: false, reason: null });
            let users_mental_health_distribution = { ...registerInfo.users_mental_health_distribution, [name]: parseInt(mental_health_percentage) };
            setRegisterInfo({ ...registerInfo, users_mental_health_distribution: users_mental_health_distribution });
          }
        }
      }
    }
  
    const closeErrorSnackBar = () => {
      setErrorSnackBar({ ...errorSnackBar, "open": false });
    }
  
    const [errorSnackBar, setErrorSnackBar] = useState<SnackBarProp>({
      open: false,
      onClose: closeErrorSnackBar,
      message: "",
    });
  
    const closeRegisterSuccessfullyDialog = () => {
      setOpenRegisterSuccessfullyDialog(false);
      if (props.onClose !== undefined) {
        props.onClose();
      }
      
      // navigate("/login");
    }

    const directToPlans = () => {
      navigate("/plans")
    }
  
    return (
      <>
        <SimpleSnackBar open={errorSnackBar.open} onClose={closeErrorSnackBar} message={errorSnackBar.message} />
        <AlertDialog open={openRegisterSuccessfullyDialog} onClose={closeRegisterSuccessfullyDialog} message={props.data !== undefined ? t("requestDemoTable.message_update_success") : t("requestDemoTable.message_submit_success")} />
        <StyledLoginCard>
          {isSubmitting && <StyledCircularProgress />}
          <Typography gutterBottom variant="h5" component="h2">{props.data === undefined ? t("requestDemoTable.text_apply_acc") : t("requestDemoTable.text_change_apply_details")}
            {props.data === undefined && <Button
              href="/centerregister"
              variant="outlined"
              color="primary"
              style={{ fontSize: 24, marginLeft: 10 }}
            >
              {t("register.text_quick_sign_up")}
            </Button>}
          </Typography>
          {props.data === undefined &&
            <Typography variant="subtitle1" color="primary">
              {t("requestDemoTable.text_thankyou_1")}<br /><br />{t("requestDemoTable.text_thankyou_2")}
            </Typography>
          }
          <StyledLoginForm autoComplete="off">
            <TextField
              error={companyNameError.isError}
              id="company_name"
              label={t("requestDemoTable.label_center")}
              onChange={(e) => onRegisterFieldChange('company_name', e.target.value)}
              margin="normal"
              helperText={companyNameError.isError && companyNameError.reason}
              defaultValue={registerInfo.company_name}
            />
  
            <TextField
              required
              error={emailError.isError}
              id="email"
              label={t("acc_key.email_address")}
              onChange={(e) => onRegisterFieldChange('email', e.target.value)}
              margin="normal"
              helperText={emailError.isError && emailError.reason}
              defaultValue={registerInfo.email}
            />
            {props.data === undefined &&
              <TextField
                required
                error={confirmEmailError.isError}
                id="email_confirmation"
                label={t("acc_key.email_address_confirm")}
                onChange={(e) => onRegisterFieldChange('email_confirmation', e.target.value)}
                margin="normal"
                helperText={confirmEmailError.isError && confirmEmailError.reason}
                defaultValue={registerInfo.email_confirmation}
              />}
  
            <TextField
              error={nameError.isError}
              id="first_name"
              label={t("requestDemoTable.label_contact_name")}
              onChange={(e) => onRegisterFieldChange('name', e.target.value)}
              margin="normal"
              helperText={nameError.isError && nameError.reason}
              defaultValue={registerInfo.name}
            />
  
            <TextField
              id="referralCode"
              label={t("common.referral_code_optional")}
              onChange={(e) => onRegisterFieldChange("referralCode", e.target.value)}
              margin="normal"
              defaultValue={registerInfo.referralCode}
            />
  
            <TextField
              error={contactNumberError.isError}
              id="contact_number"
              label={t("requestDemoTable.label_phone")}
              onChange={(e) => onRegisterFieldChange('contact_number', e.target.value)}
              margin="normal"
              helperText={contactNumberError.isError && contactNumberError.reason}
              defaultValue={registerInfo.contact_number}
            />
  
            <TextField
              id="occupation"
              label={t("common.occupation")}
              onChange={(e) => onRegisterFieldChange('occupation', e.target.value)}
              margin="normal"
              defaultValue={registerInfo.occupation}
            />
  
            <TextField
              error={companyTypeError.isError}
              id="company_type"
              label={t("requestDemoTable.label_center_type")}
              onChange={(e) => onRegisterFieldChange('company_type', e.target.value)}
              margin="normal"
              select
              hidden={true}
              helperText={companyTypeError.isError && companyTypeError.reason}
              defaultValue={registerInfo.company_type}
            >
              {CompanyTypeOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
  
            <div hidden={registerInfo.company_type !== "other"}
            >
              <TextField
                error={companyTypeError.isError}
                id="company_type_other"
                label={t("requestDemoTable.label_other_center_type")}
                fullWidth
                onChange={(e) => onRegisterFieldChange('company_type_other', e.target.value)}
                helperText={companyTypeError.isError && companyTypeError.reason}
                defaultValue={registerInfo.company_type_other}
                style={{ marginTop: 16 }}
              >
              </TextField>
            </div>
  
            <TextField
              error={noOfUserError.isError}
              id="no_of_user"
              label={t("requestDemoTable.label_quota")}
              value={registerInfo.no_of_user}
              onChange={(e) => onRegisterFieldChange('no_of_user', e.target.value)}
              margin="normal"
              select
              helperText={noOfUserError.isError && noOfUserError.reason}
              defaultValue="below_100"
            >
              {noOfUserOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
  
            <TextField
              id="company_address"
              label={t("requestDemoTable.label_center_address")}
              onChange={(e) => onRegisterFieldChange("company_address", e.target.value)}
              margin="normal"
              defaultValue={registerInfo.company_address}
            />
  
            <Grid container spacing={1} style={{ marginTop: 16 }}>
              <Grid item xs={12}>
                <InputLabel>
                  {t("requestDemoTable.label_member_birth")}
                </InputLabel>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  error={ageGroupError.isError}
                  id="users_age_distribution_55"
                  label="55-65"
                  value={registerInfo.users_age_distribution !== undefined && registerInfo.users_age_distribution.group_55_65}
                  onChange={onUsersAgeDistributionChange('group_55_65')}
                  // helperText={ageGroupError.isError && ageGroupError.reason}
                  type="number"
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  error={ageGroupError.isError}
                  id="users_age_distribution_65"
                  label="65-75"
                  value={registerInfo.users_age_distribution !== undefined && registerInfo.users_age_distribution.group_65_75}
                  onChange={onUsersAgeDistributionChange('group_65_75')}
                  // helperText={ageGroupError.isError && ageGroupError.reason}
                  type="number"
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  error={ageGroupError.isError}
                  id="users_age_distribution_75"
                  label="75-85"
                  value={registerInfo.users_age_distribution !== undefined && registerInfo.users_age_distribution.group_75_85}
                  onChange={onUsersAgeDistributionChange('group_75_85')}
                  // helperText={ageGroupError.isError && ageGroupError.reason}
                  type="number"
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  error={ageGroupError.isError}
                  id="users_age_distribution_85"
                  label={t("requestDemoTable.label_member_birth_above")}
                  value={registerInfo.users_age_distribution !== undefined && registerInfo.users_age_distribution.group_85_above}
                  onChange={onUsersAgeDistributionChange('group_85_above')}
                  // helperText={ageGroupError.isError && ageGroupError.reason}
                  type="number"
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                />
              </Grid>
              <Grid xs={12}>
                {ageGroupError.isError &&
                  <Typography color="red" style={{ paddingLeft: 20, fontSize: 10, marginTop: 5 }}>
                      {ageGroupError.reason}
                  </Typography>
                }
              </Grid>
            </Grid>
  
            {props.data === undefined ? <Grid container spacing={1} style={{ marginTop: 16 }}>
              <Grid item xs={12}>
                <InputLabel>
                  {t("requestDemoTable.label_member_mental_health")}
                </InputLabel>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  error={mentalHealthGroupError.isError}
                  id="users_mental_health_distribution_healthy"
                  label={t("requestDemoTable.label_healthy")}
                  value={registerInfo.users_mental_health_distribution !== undefined && registerInfo.users_mental_health_distribution.healthy}
                  onChange={onUsersMentalHealthDistribution('healthy')}
                  type="number"
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  // helperText={mentalHealthGroupError.isError && mentalHealthGroupError.reason}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  error={mentalHealthGroupError.isError}
                  id="users_mental_health_distribution_suspect"
                  label={t("requestDemoTable.label_health_suspect")}
                  value={registerInfo.users_mental_health_distribution !== undefined && registerInfo.users_mental_health_distribution.suspect}
                  onChange={onUsersMentalHealthDistribution('suspect')}
                  type="number"
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  // helperText={mentalHealthGroupError.isError && mentalHealthGroupError.reason}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  error={mentalHealthGroupError.isError}
                  id="users_mental_health_distribution_early"
                  label={t("requestDemoTable.label_health_early")}
                  value={registerInfo.users_mental_health_distribution !== undefined && registerInfo.users_mental_health_distribution.early}
                  onChange={onUsersMentalHealthDistribution('early')}
                  type="number"
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  // helperText={mentalHealthGroupError.isError && mentalHealthGroupError.reason}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  error={mentalHealthGroupError.isError}
                  id="users_mental_health_distribution_middle"
                  label={t("requestDemoTable.label_health_middle")}
                  value={registerInfo.users_mental_health_distribution !== undefined && registerInfo.users_mental_health_distribution.middle}
                  onChange={onUsersMentalHealthDistribution('middle')}
                  type="number"
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  // helperText={mentalHealthGroupError.isError && mentalHealthGroupError.reason}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  error={mentalHealthGroupError.isError}
                  id="users_mental_health_distribution_late"
                  label={t("requestDemoTable.label_health_late")}
                  value={registerInfo.users_mental_health_distribution !== undefined && registerInfo.users_mental_health_distribution.late}
                  onChange={onUsersMentalHealthDistribution('late')}
                  type="number"
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  // helperText={mentalHealthGroupError.isError && mentalHealthGroupError.reason}
                />
              </Grid>
            </Grid> :
              <>
                <Grid container spacing={1} style={{ marginTop: 16 }}>
                  <Grid item xs={12}>
                    <InputLabel>
                      {t("requestDemoTable.label_member_mental_health")}
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      id="users_mental_health_distribution_healthy"
                      label={t("requestDemoTable.label_healthy")}
                      value={registerInfo.users_mental_health_distribution !== undefined && registerInfo.users_mental_health_distribution.healthy}
                      onChange={onUsersMentalHealthDistribution('healthy')}
                      type="number"
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      // helperText={mentalHealthGroupError.isError && mentalHealthGroupError.reason}
                      error={mentalHealthGroupError.isError}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      id="users_mental_health_distribution_suspect"
                      label={t("requestDemoTable.label_health_suspect")}
                      value={registerInfo.users_mental_health_distribution !== undefined && registerInfo.users_mental_health_distribution.suspect}
                      onChange={onUsersMentalHealthDistribution('suspect')}
                      type="number"
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      // helperText={mentalHealthGroupError.isError && mentalHealthGroupError.reason}
                      error={mentalHealthGroupError.isError}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      id="users_mental_health_distribution_early"
                      label={t("requestDemoTable.label_health_early")}
                      value={registerInfo.users_mental_health_distribution !== undefined && registerInfo.users_mental_health_distribution.early}
                      onChange={onUsersMentalHealthDistribution('early')}
                      type="number"
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      // helperText={mentalHealthGroupError.isError && mentalHealthGroupError.reason}
                      error={mentalHealthGroupError.isError}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={1} style={{ marginTop: 16 }}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      id="users_mental_health_distribution_middle"
                      label={t("requestDemoTable.label_health_middle")}
                      value={registerInfo.users_mental_health_distribution !== undefined && registerInfo.users_mental_health_distribution.middle}
                      onChange={onUsersMentalHealthDistribution('middle')}
                      type="number"
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      // helperText={mentalHealthGroupError.isError && mentalHealthGroupError.reason}
                      error={mentalHealthGroupError.isError}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      id="users_mental_health_distribution_late"
                      label={t("requestDemoTable.label_health_late")}
                      value={registerInfo.users_mental_health_distribution !== undefined && registerInfo.users_mental_health_distribution.late}
                      onChange={onUsersMentalHealthDistribution('late')}
                      type="number"
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      // helperText={mentalHealthGroupError.isError && mentalHealthGroupError.reason}
                      error={mentalHealthGroupError.isError}
                    />
                  </Grid>
                  <Grid xs={12}>
                    {mentalHealthGroupError.isError &&
                      <Typography color="red" style={{ paddingLeft: 20, fontSize: 10, marginTop: 5 }}>
                          {mentalHealthGroupError.reason}
                      </Typography>
                    }
                  </Grid>
                </Grid></>}
            <CardActions style={{ marginLeft: 'auto' }}>
              <Button onClick={props.data ? props.onClose : directToPlans} color="primary">{t("butt.cancel")}</Button>
              <Button type="submit"  variant="contained" color="primary" onClick={onLoginButtonClick} disabled={isSubmitting}>{t("butt.submit")}</Button>
            </CardActions>
          </StyledLoginForm>
        </StyledLoginCard>
      </>
    )
}